import {
  computeNetIntensity,
  inferConstellationNodeState,
  type BehaviorConstellationState,
  type ConstellationEvidenceItem,
  type CoreDomain,
  type ObservationPolarity,
} from "@/lib/livingConstellation";

export type BehaviorRow = {
  id: string;
  name: string;
  theme: CoreDomain;
};

export type PracticeSummaryRow = {
  behavior_id: string;
  meeting_id: string;
  meeting_start_time: string;
  execution_level: "absent" | "present" | "strong" | null;
  execution_confidence: "low" | "medium" | "high" | null;
  behavior_insights: string[] | null;
  transcript_themes: string[] | null;
  meetings: { title: string | null; start_time: string | null } | null;
};

export type LeaderBehaviorRow = {
  behavior_id: string;
  phase: string | null;
};

function parseConfidence(confidence: "low" | "medium" | "high" | null): number {
  if (confidence === "high") return 1;
  if (confidence === "medium") return 0.8;
  if (confidence === "low") return 0.6;
  return 0.75;
}

function parseSupportWeight(level: "absent" | "present" | "strong" | null): number {
  if (level === "strong") return 1;
  if (level === "present") return 0.7;
  return 0;
}

function parseOppositionWeight(level: "absent" | "present" | "strong" | null): number {
  if (level === "absent") return 0.9;
  return 0;
}

function toEvidenceItem(row: PracticeSummaryRow, polarity: ObservationPolarity): ConstellationEvidenceItem {
  const insights = Array.isArray(row.behavior_insights) ? row.behavior_insights : [];
  const excerpt = insights.find((item) => typeof item === "string" && item.trim().length > 0)?.trim() || null;
  const confidence = row.execution_confidence ?? "medium";
  const contextTags = Array.isArray(row.transcript_themes) ? row.transcript_themes.slice(0, 4) : [];

  return {
    meetingId: row.meeting_id,
    meetingTitle: row.meetings?.title || "Meeting",
    meetingStartTime: row.meeting_start_time,
    polarity,
    sourceType: "transcript",
    excerpt,
    confidence,
    contextTags,
  };
}

export function deriveConstellationStateFromPracticeSummaries(params: {
  behaviors: BehaviorRow[];
  summaryRows: PracticeSummaryRow[];
  leaderBehaviorRows: LeaderBehaviorRow[];
  historyStart: string;
  windowStart: string;
}): BehaviorConstellationState[] {
  const { behaviors, summaryRows, leaderBehaviorRows, windowStart } = params;

  const phaseByBehaviorId = new Map(
    leaderBehaviorRows.map((row) => [row.behavior_id, row.phase]),
  );

  return behaviors.map((behavior) => {
    const rowsForBehavior = summaryRows.filter((row) => row.behavior_id === behavior.id);

    let supportScore = 0;
    let oppositionScore = 0;
    let lastSupportedAt: string | null = null;
    let lastOpposedAt: string | null = null;

    const supportingMeetingIds = new Set<string>();
    const opposingMeetingIds = new Set<string>();
    const contextTags = new Set<string>();

    const recentEvidence: ConstellationEvidenceItem[] = [];
    let supportingMeetingCount30d = 0;
    let opposingMeetingCount30d = 0;

    rowsForBehavior.forEach((row) => {
      const supportWeight = parseSupportWeight(row.execution_level) * parseConfidence(row.execution_confidence);
      const oppositionWeight = parseOppositionWeight(row.execution_level) * parseConfidence(row.execution_confidence);

      const rowTime = new Date(row.meeting_start_time).getTime();
      const inThirtyDayWindow = rowTime >= new Date(windowStart).getTime();

      if (supportWeight > 0) {
        supportScore += supportWeight;
        supportingMeetingIds.add(row.meeting_id);
        if (!lastSupportedAt || row.meeting_start_time > lastSupportedAt) lastSupportedAt = row.meeting_start_time;
        if (inThirtyDayWindow) supportingMeetingCount30d += 1;
        recentEvidence.push(toEvidenceItem(row, "supporting"));
      }

      if (oppositionWeight > 0) {
        oppositionScore += oppositionWeight;
        opposingMeetingIds.add(row.meeting_id);
        if (!lastOpposedAt || row.meeting_start_time > lastOpposedAt) lastOpposedAt = row.meeting_start_time;
        if (inThirtyDayWindow) opposingMeetingCount30d += 1;
        recentEvidence.push(toEvidenceItem(row, "opposing"));
      }

      if (Array.isArray(row.transcript_themes)) {
        row.transcript_themes.slice(0, 4).forEach((tag) => {
          if (typeof tag === "string" && tag.trim().length > 0) contextTags.add(tag.trim());
        });
      }
    });

    const state = inferConstellationNodeState({
      supportingCount: supportingMeetingIds.size,
      opposingCount: opposingMeetingIds.size,
      hasPriorPositivePattern: supportingMeetingIds.size >= 2,
    });

    const normalizedSupportScore = Math.min(1, supportScore / 4);
    const normalizedOppositionScore = Math.min(1, oppositionScore / 4);
    const netIntensity = computeNetIntensity({
      supportScore: normalizedSupportScore,
      oppositionScore: normalizedOppositionScore,
      state,
    });

    const evidenceMeetingIds = Array.from(new Set([
      ...Array.from(supportingMeetingIds),
      ...Array.from(opposingMeetingIds),
    ])).slice(0, 8);

    return {
      behaviorId: behavior.id,
      behaviorName: behavior.name,
      theme: behavior.theme,
      supportScore: normalizedSupportScore,
      oppositionScore: normalizedOppositionScore,
      netIntensity,
      state,
      supportingMeetingCount30d,
      opposingMeetingCount30d,
      lastSupportedAt,
      lastOpposedAt,
      evidenceMeetingIds,
      contextTags: Array.from(contextTags).slice(0, 5),
      recentEvidence: recentEvidence.slice(0, 6),
      phase: phaseByBehaviorId.get(behavior.id) ?? null,
    } satisfies BehaviorConstellationState;
  });
}
